import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Callout,
  EmptyState,
  IconReceipt,
  IconSearch,
  Input,
  ListPage,
  SectionHead,
  Select,
  Spinner,
  StatusLabel,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tag,
  type Tone,
  TwoLine,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  FxSnapshot,
  SubscriptionRow,
  SubscriptionsResponse,
  UnbilledUsageResponse,
  UnbilledUsageRow,
  formatDate,
  formatMoney,
  formatPercent,
  numberValue,
  titleCase,
} from './billingAdminShared';
import {
  ALL,
  DEFAULT_SUBSCRIPTION_SORT,
  type SubscriptionSortKey,
  filterSubscriptions,
  formatCalls,
  formatMinutes,
  fxCaption,
  marginTone,
  nextSort,
  sortSubscriptions,
  subscriptionPlanOptions,
  subscriptionStatusOptions,
  subscriptionStatusTone,
  unbilledDescription,
} from './subscriptionsModel';

const BILLING_REQUEST_TIMEOUT_MS = 30_000;

const SUBSCRIPTION_COLUMNS: {
  key: SubscriptionSortKey;
  label: string;
  align?: 'right';
  width?: number;
}[] = [
  { key: 'store', label: 'Store' },
  { key: 'plan', label: 'Plan', width: 176 },
  { key: 'status', label: 'Status', width: 120 },
  { key: 'monthlyPrice', label: 'Monthly price', align: 'right', width: 128 },
  { key: 'periodEnd', label: 'Period end', width: 120 },
  { key: 'minutes', label: 'Call minutes', align: 'right', width: 120 },
  { key: 'cost', label: 'Cost this period', align: 'right', width: 176 },
  { key: 'costPerMinute', label: 'Retell $/min', align: 'right', width: 128 },
  { key: 'margin', label: 'Margin', align: 'right', width: 96 },
  {
    key: 'lifetimeRevenue',
    label: 'Lifetime revenue / cost',
    align: 'right',
    width: 208,
  },
];

const marginTextClass: Record<Tone, string> = {
  neutral: 'text-v2-fg-3',
  accent: 'text-v2-accent',
  success: 'text-v2-success',
  warning: 'text-v2-warning',
  danger: 'text-v2-danger',
};

const CELL = 'py-2';

export default function SubscriptionsTab() {
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [fx, setFx] = useState<FxSnapshot | null>(null);
  const [unbilled, setUnbilled] = useState<UnbilledUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [planFilter, setPlanFilter] = useState(ALL);
  const [sort, setSort] = useState(DEFAULT_SUBSCRIPTION_SORT);
  const requestControllerRef = useRef<AbortController | null>(null);

  const loadData = useCallback(async () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const timeout = window.setTimeout(
      () => controller.abort(),
      BILLING_REQUEST_TIMEOUT_MS,
    );
    setLoading(true);
    setError('');
    try {
      const [subscriptionsResponse, unbilledResponse] = await Promise.all([
        api.get(apiEndpoints.getAdminSubscriptions(), {
          signal: controller.signal,
        }),
        api.get(apiEndpoints.getAdminUnbilledUsage(), {
          signal: controller.signal,
        }),
      ]);
      if (!subscriptionsResponse?.ok || !unbilledResponse?.ok) {
        throw new Error('The subscriptions could not be loaded.');
      }
      const [subscriptionsData, unbilledData] = (await Promise.all([
        subscriptionsResponse.json(),
        unbilledResponse.json(),
      ])) as [SubscriptionsResponse, UnbilledUsageResponse];
      setSubscriptions(
        Array.isArray(subscriptionsData.results)
          ? subscriptionsData.results
          : [],
      );
      setFx(subscriptionsData.fx ?? null);
      setUnbilled(
        Array.isArray(unbilledData.results) ? unbilledData.results : [],
      );
    } catch (loadError) {
      if (requestControllerRef.current !== controller) return;
      console.error('Failed to load admin subscriptions:', loadError);
      setError(
        loadError instanceof DOMException && loadError.name === 'AbortError'
          ? 'The subscriptions request timed out. Please try again.'
          : loadError instanceof Error
            ? loadError.message
            : 'The subscriptions could not be loaded.',
      );
    } finally {
      window.clearTimeout(timeout);
      if (requestControllerRef.current === controller) {
        requestControllerRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadData();
    return () => {
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
    };
  }, [loadData]);

  const statuses = useMemo(
    () => subscriptionStatusOptions(subscriptions),
    [subscriptions],
  );
  const plans = useMemo(
    () => subscriptionPlanOptions(subscriptions),
    [subscriptions],
  );

  const filteredSubscriptions = useMemo(
    () =>
      sortSubscriptions(
        filterSubscriptions(subscriptions, {
          search,
          status: statusFilter,
          plan: planFilter,
        }),
        sort.key,
        sort.direction,
      ),
    [planFilter, search, sort, statusFilter, subscriptions],
  );

  const renderSubscriptionRow = (row: SubscriptionRow) => {
    const currency = row.currency || 'USD';
    const costCurrency = row.fx?.converted === false ? 'USD' : currency;
    const retellCost = numberValue(row.retell?.current_period?.cost_cents);
    const openTableCost = numberValue(row.opentable?.monthly_cost_cents);
    const perMinute =
      row.retell_usd_cents_per_minute == null
        ? null
        : formatMoney(row.retell_usd_cents_per_minute, 'USD', 4);
    return (
      <TR key={row.id}>
        <TD className={CELL}>
          <div className="truncate font-medium text-v2-fg">
            {row.store?.name || 'Unnamed store'}
          </div>
          {(row.group?.name || row.provider?.linked) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-v2-12 text-v2-fg-3">
              {row.group?.name && <span>{row.group.name}</span>}
              {row.provider?.linked && (
                <Tag tone="accent">{titleCase(row.provider.name)} linked</Tag>
              )}
            </div>
          )}
        </TD>
        <TD className={CELL}>
          <TwoLine
            primary={row.plan?.name || row.plan?.code || '—'}
            secondary={row.plan?.code}
          />
        </TD>
        <TD className={CELL}>
          <StatusLabel tone={subscriptionStatusTone(row.status)}>
            {titleCase(row.status)}
          </StatusLabel>
        </TD>
        <TD num align="right" className={CELL}>
          <TwoLine
            primary={formatMoney(row.pricing?.monthly_value_cents, currency)}
            secondary={currency}
          />
        </TD>
        <TD num className={CELL}>
          {formatDate(row.current_period?.end)}
        </TD>
        <TD num align="right" className={CELL}>
          <TwoLine
            primary={formatMinutes(
              row.retell?.current_period?.duration_minutes,
            )}
            secondary={formatCalls(row.retell?.current_period?.call_count)}
          />
        </TD>
        <TD num align="right" className={CELL}>
          <TwoLine
            primary={formatMoney(row.current_cost_cents, costCurrency, 2)}
            secondary={`${formatMoney(retellCost, costCurrency, 2)} Retell`}
          />
          {row.opentable?.connected && (
            <div className="mt-1 flex items-center justify-end gap-1.5 text-v2-12 text-v2-fg-3">
              <Tag>OpenTable</Tag>
              <span>{formatMoney(openTableCost, costCurrency, 2)}</span>
            </div>
          )}
        </TD>
        <TD num align="right" className={CELL}>
          {perMinute == null ? (
            '—'
          ) : (
            <TwoLine primary={perMinute} secondary="USD" />
          )}
        </TD>
        <TD num align="right" className={CELL}>
          <span
            className={`font-semibold ${
              marginTextClass[marginTone(row.current_margin_percent)]
            }`}
          >
            {formatPercent(row.current_margin_percent)}
          </span>
        </TD>
        <TD num align="right" className={CELL}>
          <div className="font-medium">
            <span className="text-v2-success">
              {formatMoney(row.lifetime_paid_revenue_cents, currency)}
            </span>
            <span className="mx-1 text-v2-fg-3">/</span>
            <span className="text-v2-danger">
              {formatMoney(row.lifetime_cost_cents, costCurrency)}
            </span>
          </div>
          <div className="text-v2-12 text-v2-fg-3">
            revenue / cost ·{' '}
            {costCurrency === currency
              ? currency
              : `${currency} / ${costCurrency}`}
          </div>
        </TD>
      </TR>
    );
  };

  const renderUnbilledRow = (row: UnbilledUsageRow) => (
    <TR key={row.store?.id ?? row.store?.name}>
      <TD className={CELL}>
        <TwoLine
          primary={row.store?.name || 'Unnamed store'}
          secondary={row.group?.name}
        />
      </TD>
      <TD className={CELL}>
        {row.subscription ? (
          <>
            <div className="font-medium text-v2-fg">
              {row.subscription.plan_name || row.subscription.plan_code || '—'}
            </div>
            <StatusLabel
              size="sm"
              tone={subscriptionStatusTone(row.subscription.status)}
            >
              {titleCase(row.subscription.status)}
            </StatusLabel>
          </>
        ) : (
          <StatusLabel tone="danger">No subscription</StatusLabel>
        )}
      </TD>
      <TD num align="right" className={CELL}>
        <TwoLine
          primary={formatMinutes(row.usage?.duration_minutes)}
          secondary={formatCalls(row.usage?.call_count)}
        />
      </TD>
      <TD num align="right" className={`${CELL} font-medium`}>
        {formatMoney(row.usage?.cost_cents, 'USD', 2)}
      </TD>
      <TD className={CELL}>
        {row.last_invoice ? (
          <TwoLine
            primary={formatDate(row.last_invoice.created_at)}
            secondary={`${titleCase(row.last_invoice.status)} · ${formatMoney(
              row.last_invoice.total_cents,
            )}`}
          />
        ) : (
          <StatusLabel tone="warning">Never billed</StatusLabel>
        )}
      </TD>
    </TR>
  );

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div
        role="status"
        className="flex min-h-[320px] items-center justify-center gap-2 text-v2-13 text-v2-fg-3"
      >
        <Spinner />
        Loading subscriptions…
      </div>
    );
  } else if (error) {
    body = (
      <div className="p-4 lg:p-6">
        <Callout tone="danger" title={error}>
          <Button
            size="sm"
            className="mt-2"
            onClick={() => {
              void loadData();
            }}
          >
            Try again
          </Button>
        </Callout>
      </div>
    );
  } else {
    const rateNote = fxCaption(fx);
    body = (
      <>
        <section aria-label="Store subscriptions">
          <div className="px-4 pb-3 pt-5 lg:px-6">
            <SectionHead
              title="Store subscriptions"
              description={`${filteredSubscriptions.length} of ${subscriptions.length} subscriptions`}
            />
            {rateNote && (
              <p className="mt-2 text-v2-12 text-v2-fg-3">{rateNote}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="w-full min-w-0 sm:w-[280px]">
                <Input
                  type="search"
                  size="sm"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search store, group, or plan"
                  aria-label="Search subscriptions"
                  leading={<IconSearch size={14} />}
                />
              </div>
              <Select
                size="sm"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
                className="w-[168px] shrink-0"
              >
                <option value={ALL}>All statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {titleCase(status)}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                value={planFilter}
                onChange={(event) => setPlanFilter(event.target.value)}
                aria-label="Filter by plan"
                className="w-[168px] shrink-0"
              >
                <option value={ALL}>All plans</option>
                {plans.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {subscriptions.length === 0 ? (
            <EmptyState
              compact
              icon={<IconReceipt />}
              title="No current subscriptions were returned."
            />
          ) : filteredSubscriptions.length === 0 ? (
            <EmptyState
              compact
              icon={<IconSearch />}
              title="No subscriptions match the current filters."
            />
          ) : (
            <div className="v2-scroll overflow-x-auto border-t border-v2-line">
              <Table fixed className="min-w-[1488px]">
                <THead>
                  <tr>
                    {SUBSCRIPTION_COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        align={column.align}
                        sortable
                        active={sort.key === column.key}
                        dir={sort.direction}
                        onSort={() =>
                          setSort((current) => nextSort(current, column.key))
                        }
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {filteredSubscriptions.map(renderSubscriptionRow)}
                </TBody>
              </Table>
            </div>
          )}
        </section>

        <section
          aria-label="Unbilled usage (last 30 days)"
          className="border-t border-v2-line"
        >
          <div className="px-4 pb-3 pt-5 lg:px-6">
            <SectionHead
              title="Unbilled usage (last 30 days)"
              description={unbilledDescription(unbilled.length)}
            />
          </div>
          {unbilled.length === 0 ? (
            <EmptyState
              compact
              icon={<IconReceipt />}
              title="Every store with recent call usage has been billed in the last 30 days."
            />
          ) : (
            <div className="v2-scroll overflow-x-auto border-t border-v2-line">
              <Table fixed className="min-w-[960px]">
                <THead>
                  <tr>
                    <TH>Store</TH>
                    <TH width={240}>Subscription</TH>
                    <TH align="right" width={144}>
                      Call minutes
                    </TH>
                    <TH align="right" width={144}>
                      Retell cost
                    </TH>
                    <TH width={200}>Last invoice</TH>
                  </tr>
                </THead>
                <TBody>{unbilled.map(renderUnbilledRow)}</TBody>
              </Table>
            </div>
          )}
        </section>
      </>
    );
  }

  return (
    <ListPage
      title="Subscriptions"
      description="Store-level subscriptions, usage, and billing gaps."
    >
      {body}
    </ListPage>
  );
}
